// C:\IntegraNube_WebStore\src\pages\Carrito.js
'use strict';

import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteIcon,
  ImageNotSupportedOutlined as NoImageIcon,
  LocalOfferOutlined as OfferIcon,
  Remove as RemoveIcon,
  SavingsOutlined as SavingsIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  ShoppingCartCheckoutOutlined as CheckoutIcon,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';

const CART_STORAGE_KEY = 'integranube_webstore_cart';
const CART_EVENT = 'integranube-cart-updated';

const CLP = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/* ===================================================================== */
/*  Helpers                                                              */
/* ===================================================================== */

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const cleanString = (value) => {
  const text = String(value ?? '').trim();
  return text || null;
};

function resolveTiendaData(source) {
  if (!source || typeof source !== 'object') {
    return {};
  }

  if (
    source.status &&
    source.data &&
    typeof source.data === 'object'
  ) {
    return source.data;
  }

  if (
    source.data &&
    typeof source.data === 'object' &&
    (
      source.data.branding ||
      source.data.nombre ||
      source.data.funciones ||
      source.data.ecommerce
    )
  ) {
    return source.data;
  }

  return source;
}

function readCart() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(
      CART_STORAGE_KEY
    );

    const cart = raw ? JSON.parse(raw) : [];

    return Array.isArray(cart)
      ? cart
      : [];
  } catch (error) {
    console.warn(
      '[Carrito] No fue posible leer el carrito:',
      error
    );

    return [];
  }
}

function saveCart(cart) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    CART_STORAGE_KEY,
    JSON.stringify(cart)
  );

  window.dispatchEvent(
    new CustomEvent(CART_EVENT, {
      detail: cart,
    })
  );
}

function getQuantity(item) {
  return Math.max(
    0,
    toNum(
      item?.cantidad ??
      item?.quantity,
      0
    )
  );
}

function getCurrentPrice(item) {
  return Math.max(
    0,
    toNum(
      item?.precio ??
      item?.precio_bruto ??
      item?.precioDetalle?.bruto ??
      item?.precioNeto ??
      item?.precio_neto,
      0
    )
  );
}

function getNormalPrice(item) {
  const normal = toNum(
    item?.precioNormal ??
    item?.precio_normal ??
    item?.precioNormalDetalle?.bruto ??
    item?.precio_lista ??
    item?.precioLista,
    0
  );

  const actual = getCurrentPrice(item);

  return normal > actual
    ? normal
    : actual;
}

function getOfferInfo(item) {
  const precio = getCurrentPrice(item);
  const precioNormal = getNormalPrice(item);

  const enOferta =
    precio > 0 &&
    precioNormal > precio;

  const ahorroUnitario = enOferta
    ? Math.max(0, precioNormal - precio)
    : 0;

  const descuentoPorcentaje =
    enOferta && precioNormal > 0
      ? Math.max(
          1,
          Math.round(
            (ahorroUnitario / precioNormal) * 100
          )
        )
      : 0;

  return {
    precio,
    precioNormal,
    enOferta,
    ahorroUnitario,
    descuentoPorcentaje,
  };
}

function getStockInfo(item) {
  const rawStock =
    item?.stock ??
    item?.stockDetalle?.disponible;

  const stock = Number(rawStock);

  const stockKnown =
    rawStock !== null &&
    rawStock !== undefined &&
    Number.isFinite(stock);

  return {
    stockKnown,
    stock: stockKnown
      ? Math.max(0, stock)
      : null,
  };
}

function getProductPath(item) {
  const slug =
    cleanString(
      item?.slug
    );

  /*
   * La ficha pública usa siempre /producto/{slug}. Para carritos
   * antiguos sin slug, volvemos al catálogo en vez de fabricar
   * una URL pública por ID.
   */
  return slug
    ? `/producto/${encodeURIComponent(slug)}`
    : '/productos';
}

/* ===================================================================== */
/*  Carrito                                                              */
/* ===================================================================== */

export default function Carrito({ tienda }) {
  const navigate = useNavigate();

  const tiendaData = useMemo(
    () => resolveTiendaData(tienda),
    [tienda]
  );

  const colorPrimario =
    cleanString(
      tiendaData?.branding?.colorPrimario
    ) || '#1976d2';

  const carritoHabilitado =
    tiendaData?.funciones?.carrito !== false;

  const checkoutHabilitado =
    tiendaData?.funciones?.checkout !== false;

  const mostrarSku =
    tiendaData?.ecommerce?.mostrarSku !== false;

  const [cart, setCart] = useState([]);

  useEffect(() => {
    setCart(readCart());
  }, []);

  const resumen = useMemo(() => {
    return cart.reduce(
      (acc, item) => {
        const cantidad = getQuantity(item);
        const oferta = getOfferInfo(item);

        acc.unidades += cantidad;
        acc.total += oferta.precio * cantidad;
        acc.totalNormal +=
          oferta.precioNormal * cantidad;
        acc.ahorro +=
          oferta.ahorroUnitario * cantidad;

        if (oferta.enOferta) {
          acc.productosEnOferta += 1;
        }

        return acc;
      },
      {
        unidades: 0,
        total: 0,
        totalNormal: 0,
        ahorro: 0,
        productosEnOferta: 0,
      }
    );
  }, [cart]);

  const updateCart = (nextCart) => {
    setCart(nextCart);
    saveCart(nextCart);
  };

  const changeQuantity = (id, delta) => {
    const next = cart.map((item) => {
      if (String(item.id) !== String(id)) {
        return item;
      }

      const actual = Math.max(
        1,
        getQuantity(item) || 1
      );

      const { stockKnown, stock } =
        getStockInfo(item);

      let cantidad = Math.max(
        1,
        actual + delta
      );

      if (stockKnown) {
        cantidad = Math.min(
          cantidad,
          Math.max(1, Math.floor(stock))
        );
      }

      return {
        ...item,
        cantidad,
        quantity: cantidad,
      };
    });

    updateCart(next);
  };

  const removeItem = (id) => {
    updateCart(
      cart.filter(
        (item) =>
          String(item.id) !== String(id)
      )
    );
  };

  const clearCart = () => {
    updateCart([]);
  };

  /*
   * IMPORTANTE:
   * Los Hooks ya se ejecutaron arriba.
   * Desde aquí sí podemos hacer retornos condicionales.
   */
  if (!carritoHabilitado) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              onClick={() => navigate('/productos')}
            >
              Ver productos
            </Button>
          }
        >
          El carrito no está habilitado en esta tienda.
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#f8fafc',
        minHeight: 'calc(100vh - 80px)',
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 2.5, md: 4 } }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            spacing={1.5}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight={950}
                  sx={{ letterSpacing: '-0.035em' }}
                >
                  Tu carrito
                </Typography>

                {cart.length > 0 ? (
                  <Chip
                    size="small"
                    label={`${resumen.unidades.toLocaleString(
                      'es-CL'
                    )} ${
                      resumen.unidades === 1
                        ? 'unidad'
                        : 'unidades'
                    }`}
                    sx={{
                      bgcolor: `${colorPrimario}12`,
                      color: colorPrimario,
                      fontWeight: 800,
                    }}
                  />
                ) : null}
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.65 }}
              >
                Revisa tus productos antes de continuar con la compra.
              </Typography>
            </Box>

            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/productos')}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 800,
                color: colorPrimario,
              }}
            >
              Seguir comprando
            </Button>
          </Stack>

          {cart.length === 0 ? (
            <EmptyCart
              colorPrimario={colorPrimario}
              onProducts={() => navigate('/productos')}
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1fr) 380px',
                },
                gap: { xs: 2, lg: 2.5 },
                alignItems: 'start',
              }}
            >
              <Stack spacing={1.25}>
                {cart.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    mostrarSku={mostrarSku}
                    colorPrimario={colorPrimario}
                    onView={() =>
                      navigate(
                        getProductPath(item)
                      )
                    }
                    onIncrease={() =>
                      changeQuantity(item.id, 1)
                    }
                    onDecrease={() =>
                      changeQuantity(item.id, -1)
                    }
                    onRemove={() =>
                      removeItem(item.id)
                    }
                  />
                ))}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{
                    xs: 'stretch',
                    sm: 'center',
                  }}
                  spacing={1}
                  sx={{ pt: 0.5 }}
                >
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/productos')}
                    sx={{
                      alignSelf: {
                        xs: 'stretch',
                        sm: 'flex-start',
                      },
                      justifyContent: {
                        xs: 'center',
                        sm: 'flex-start',
                      },
                      color: colorPrimario,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    Agregar más productos
                  </Button>

                  <Button
                    color="error"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={clearCart}
                    sx={{
                      alignSelf: {
                        xs: 'stretch',
                        sm: 'flex-end',
                      },
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    Vaciar carrito
                  </Button>
                </Stack>
              </Stack>

              <CartSummary
                resumen={resumen}
                colorPrimario={colorPrimario}
                checkoutHabilitado={checkoutHabilitado}
                onCheckout={() => navigate('/finalizar-compra')}
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

/* ===================================================================== */
/*  Item del carrito                                                      */
/* ===================================================================== */

function CartItem({
  item,
  mostrarSku,
  colorPrimario,
  onView,
  onIncrease,
  onDecrease,
  onRemove,
}) {
  const cantidad = Math.max(
    1,
    getQuantity(item) || 1
  );

  const oferta = getOfferInfo(item);
  const total = oferta.precio * cantidad;
  const ahorroLinea =
    oferta.ahorroUnitario * cantidad;

  const { stockKnown, stock } =
    getStockInfo(item);

  const noPuedeAumentar =
    stockKnown &&
    cantidad >= Math.floor(stock);

  const unidad =
    cleanString(item?.unidad) || 'unidad';

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        bgcolor: '#fff',
        borderColor: oferta.enOferta
          ? `${colorPrimario}28`
          : '#e5e7eb',
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          p: { xs: 1.5, sm: 2 },
          '&:last-child': {
            pb: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Stack
          direction={{ xs: 'row', sm: 'row' }}
          spacing={{ xs: 1.25, sm: 1.75 }}
          alignItems="center"
        >
          <Box
            onClick={onView}
            sx={{
              width: { xs: 86, sm: 112 },
              height: { xs: 86, sm: 112 },
              borderRadius: 2,
              border: '1px solid #eef2f7',
              bgcolor: '#fff',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
              flex: '0 0 auto',
            }}
          >
            {item.imagen ? (
              <Box
                component="img"
                src={item.imagen}
                alt={item.nombre || 'Producto'}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  p: 0.75,
                }}
              />
            ) : (
              <NoImageIcon
                sx={{
                  fontSize: { xs: 40, sm: 52 },
                  color: '#d1d5db',
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              {oferta.enOferta ? (
                <Chip
                  size="small"
                  icon={
                    <OfferIcon
                      sx={{
                        fontSize: '15px !important',
                      }}
                    />
                  }
                  label={`${oferta.descuentoPorcentaje}% OFF`}
                  sx={{
                    height: 24,
                    bgcolor: '#ecfdf5',
                    color: '#047857',
                    fontWeight: 900,
                    '& .MuiChip-icon': {
                      color: '#047857',
                    },
                  }}
                />
              ) : null}
            </Stack>

            <Typography
              variant="body1"
              fontWeight={900}
              onClick={onView}
              sx={{
                mt: oferta.enOferta ? 0.6 : 0,
                cursor: 'pointer',
                lineHeight: 1.3,
                color: '#111827',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {item.nombre || 'Producto'}
            </Typography>

            {mostrarSku && item.sku ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 0.35,
                }}
              >
                SKU: {item.sku}
              </Typography>
            ) : null}

            <Stack
              direction="row"
              spacing={0.8}
              alignItems="baseline"
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.8 }}
            >
              {oferta.enOferta ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9ca3af',
                    textDecoration: 'line-through',
                    fontWeight: 700,
                  }}
                >
                  {CLP.format(oferta.precioNormal)}
                </Typography>
              ) : null}

              <Typography
                variant="body2"
                sx={{
                  color: oferta.enOferta
                    ? colorPrimario
                    : '#111827',
                  fontWeight: 900,
                }}
              >
                {CLP.format(oferta.precio)} c/u
              </Typography>

              <Typography
                variant="caption"
                sx={{ color: '#9ca3af' }}
              >
                / {unidad}
              </Typography>
            </Stack>

            {oferta.enOferta ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.35,
                  color: '#047857',
                  fontWeight: 800,
                }}
              >
                Ahorras {CLP.format(ahorroLinea)} en esta línea
              </Typography>
            ) : null}

            {stockKnown ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.35,
                  color: stock > 0
                    ? '#6b7280'
                    : '#b91c1c',
                }}
              >
                {stock > 0
                  ? `${Math.floor(stock).toLocaleString(
                      'es-CL'
                    )} disponible${
                      Math.floor(stock) === 1
                        ? ''
                        : 's'
                    }`
                  : 'Sin stock disponible'}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              display: {
                xs: 'none',
                sm: 'block',
              },
              minWidth: 150,
              textAlign: 'right',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#9ca3af',
                fontWeight: 700,
              }}
            >
              Subtotal
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mt: 0.15,
                whiteSpace: 'nowrap',
                color: '#111827',
                fontWeight: 950,
              }}
            >
              {CLP.format(total)}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Paper
            variant="outlined"
            sx={{
              height: 42,
              minWidth: 126,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 0.25,
              bgcolor: '#fff',
            }}
          >
            <IconButton
              size="small"
              disabled={cantidad <= 1}
              onClick={onDecrease}
              aria-label="Disminuir cantidad"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <Typography
              variant="body2"
              fontWeight={900}
            >
              {cantidad}
            </Typography>

            <IconButton
              size="small"
              disabled={noPuedeAumentar}
              onClick={onIncrease}
              aria-label="Aumentar cantidad"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Paper>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <Box
              sx={{
                display: {
                  xs: 'block',
                  sm: 'none',
                },
                textAlign: 'right',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#9ca3af' }}
              >
                Subtotal
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  fontWeight: 950,
                }}
              >
                {CLP.format(total)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              color="error"
              aria-label="Eliminar producto"
              onClick={onRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {noPuedeAumentar && stockKnown ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.8,
              color: '#6b7280',
            }}
          >
            Alcanzaste la cantidad disponible para este producto.
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ===================================================================== */
/*  Resumen                                                              */
/* ===================================================================== */

function CartSummary({
  resumen,
  colorPrimario,
  checkoutHabilitado,
  onCheckout,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        bgcolor: '#fff',
        alignSelf: 'start',
        position: {
          lg: 'sticky',
        },
        top: {
          lg: 16,
        },
        borderColor: '#e5e7eb',
      }}
    >
      <Typography
        variant="h6"
        fontWeight={950}
      >
        Resumen de compra
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          color: '#6b7280',
        }}
      >
        {resumen.unidades.toLocaleString('es-CL')}{' '}
        {resumen.unidades === 1
          ? 'unidad'
          : 'unidades'}
      </Typography>

      <Stack
        spacing={1.25}
        sx={{ mt: 2.25 }}
      >
        {resumen.ahorro > 0 ? (
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Precio normal
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  textDecoration: 'line-through',
                  fontWeight: 700,
                }}
              >
                {CLP.format(resumen.totalNormal)}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <SavingsIcon
                  sx={{
                    fontSize: 18,
                    color: '#047857',
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: '#047857',
                    fontWeight: 800,
                  }}
                >
                  Ahorro en ofertas
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                sx={{
                  color: '#047857',
                  fontWeight: 900,
                }}
              >
                -{CLP.format(resumen.ahorro)}
              </Typography>
            </Stack>
          </>
        ) : (
          <Stack
            direction="row"
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Productos
            </Typography>

            <Typography
              variant="body2"
              fontWeight={800}
            >
              {resumen.unidades}
            </Typography>
          </Stack>
        )}

        <Divider />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          spacing={2}
        >
          <Typography
            variant="h6"
            fontWeight={900}
          >
            Total
          </Typography>

          <Typography
            variant="h4"
            sx={{
              color: colorPrimario,
              fontWeight: 950,
              letterSpacing: '-0.035em',
            }}
          >
            {CLP.format(resumen.total)}
          </Typography>
        </Stack>

        {resumen.ahorro > 0 ? (
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: '#ecfdf5',
              border: '1px solid #d1fae5',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#047857',
                fontWeight: 800,
                textAlign: 'center',
              }}
            >
              Estás ahorrando {CLP.format(resumen.ahorro)} en esta compra
            </Typography>
          </Box>
        ) : null}

        {checkoutHabilitado ? (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CheckoutIcon />}
            onClick={onCheckout}
            sx={{
              mt: 0.5,
              minHeight: 50,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 900,
              boxShadow: 'none',
              bgcolor: colorPrimario,
              '&:hover': {
                bgcolor: colorPrimario,
                filter: 'brightness(0.92)',
                boxShadow: 'none',
              },
            }}
          >
            Finalizar compra
          </Button>
        ) : (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
          >
            La compra online no está habilitada en esta tienda.
          </Alert>
        )}

        <Typography
          variant="caption"
          sx={{
            color: '#9ca3af',
            textAlign: 'center',
            lineHeight: 1.5,
          }}
        >
          El total corresponde a los precios vigentes mostrados en la tienda.
        </Typography>
      </Stack>
    </Paper>
  );
}

/* ===================================================================== */
/*  Carrito vacío                                                        */
/* ===================================================================== */

function EmptyCart({
  colorPrimario,
  onProducts,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        py: { xs: 6, md: 8 },
        px: 2,
        borderRadius: 3,
        bgcolor: '#fff',
      }}
    >
      <Stack
        spacing={1.5}
        alignItems="center"
        textAlign="center"
      >
        <Box
          sx={{
            width: 92,
            height: 92,
            borderRadius: '50%',
            bgcolor: `${colorPrimario}0d`,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ShoppingBagIcon
            sx={{
              fontSize: 48,
              color: colorPrimario,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          fontWeight={950}
        >
          Tu carrito está vacío
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 430 }}
        >
          Explora el catálogo y agrega los productos que quieras comprar.
        </Typography>

        <Button
          variant="contained"
          onClick={onProducts}
          sx={{
            mt: 0.5,
            borderRadius: 2,
            px: 3,
            minHeight: 46,
            textTransform: 'none',
            fontWeight: 900,
            boxShadow: 'none',
            bgcolor: colorPrimario,
            '&:hover': {
              bgcolor: colorPrimario,
              filter: 'brightness(0.92)',
              boxShadow: 'none',
            },
          }}
        >
          Ver productos
        </Button>
      </Stack>
    </Paper>
  );
}
